/* eslint-disable react/no-danger */
import type { AppProps, NextWebVitalsMetric } from 'next/app'
import { useEffect, useState } from 'react'
import Router, { useRouter } from 'next/router'
import Head from 'next/head'
import Script from 'next/script'
import { DefaultSeo, LogoJsonLd, OrganizationJsonLd } from 'next-seo'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import SEO from '../next-seo.config'
import '../src/globals.css'
import { handleCookiesExpiration } from '~shared/utils/handleCookiesExpiration'
import { handleUtms } from '~shared/utils/handleUtms'
import { handleReferer } from '~shared/utils/handleReferer'
import { handleLocale } from '~shared/utils/handleLocale'
import { useHandleUtms } from '~shared/hooks/useHandleUtms'
import { PATHS } from '~shared/constants/paths'
import dynamic from 'next/dynamic'
import { CookieConsent } from '~widgets/сookie-сonsent/cookie-consent'
import { golosTextVF } from '~shared/fonts/golosText'

const AskFormWidget = dynamic(
  () => import('~widgets/ask-form/ask-form-widget').then(m => m.AskFormWidget),
  { ssr: false }
)

export const App = ({ Component, pageProps, router }: AppProps) => {
  const route = useRouter()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.cookie = `current_url=${encodeURIComponent(
        window.location.href
      )}; path=/; max-age=31536000`
    }

    handleCookiesExpiration({ route })
    handleUtms({ router })
    handleReferer()
    handleLocale({ router })

    NProgress.configure({
      showSpinner: false
    })

    const start = () => {
      NProgress.start()
    }
    const end = (url: URL) => {
      NProgress.done()

      // обновляем куку при каждом переходе
      document.cookie = `current url=${encodeURIComponent(
        window.location.href
      )}; path=/; max-age=31536000`

      // pageview({ url })
    }
    Router.events.on('routeChangeStart', start)
    Router.events.on('routeChangeComplete', end)
    Router.events.on('routeChangeError', end)
    return () => {
      Router.events.off('routeChangeStart', start)
      Router.events.off('routeChangeComplete', end)
      Router.events.off('routeChangeError', end)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  useHandleUtms()

  useEffect(() => {
    const yandexTimer = setTimeout(() => {
      try {
        // Проверяем, не загружена ли уже метрика
        if (window.ym) return

        // Создаем noscript-тег для Яндекс.Метрики
        const noscript = document.createElement('noscript')
        noscript.innerHTML =
          '<div><img src="https://mc.yandex.ru/watch/106470262" style="position:absolute; left:-9999px;" alt="" /></div>'
        document.body.appendChild(noscript)

        // Создаем inline-скрипт с кодом инициализации
        const initScript = document.createElement('script')
        initScript.innerHTML = `
            (function(m,e,t,r,i,k,a){
              m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
              m[i].l=1*new Date();
              for (var j = 0; j < document.scripts.length; j++) {
                if (document.scripts[j].src === r) { return; }
              }
              k=e.createElement(t),a=e.getElementsByTagName(t)[0],
              k.async=1,k.src=r,a.parentNode.insertBefore(k,a)
            })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

            ym(92116660, "init", {
              clickmap:true,
              trackLinks:true,
              accurateTrackBounce:true,
              webvisor:true,
              ecommerce:"dataLayer"
            });
            `
        document.head.appendChild(initScript)
      } catch (e) {
        console.error('Yandex Metrika initialization error:', e)
      }
    }, 1500)
    return () => {
      clearTimeout(yandexTimer)
    }
  }, [])


  return (
    <>
      <Head>
        {/* Varioqub antiflicker */}
        <style
          dangerouslySetInnerHTML={{
            __html: '.varioqub-antiflicker {opacity: 0 !important;}'
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
                  (function(a,n,t,i,f,li,c,k,e,r){
                      a[t]=a[t]||function(){(a[t].a=a[t].a||[]).push(arguments)};n.classList.add(i);c=function(){n.classList.remove(i)};li.callback=c;a[t].antiFlicker=li;setTimeout(c,f);
                  })(window, document.documentElement, 'ymab', 'varioqub-antiflicker', 4000, { 'metrika.92116660': true });
                  `
          }}
        />

        {/* Основной скрипт Roistat */}
        <script
          id='roistat-counter'
          dangerouslySetInnerHTML={{
            __html: `(function(w, d, s, h, id) {
                w.roistatProjectId = id; w.roistatHost = h; w.roistatPage = d.location.href; w.roistatReferrer = d.referrer;
                var p = d.location.protocol == "https:" ? "https://" : "http://";
                var u = /^.*roistat_visit=[^;]+(.*)?$/.test(d.cookie) ? "/dist/module.js" : "/api/site/1.0/"+id+"/init?referrer="+encodeURIComponent(d.location.href);
                var js = d.createElement(s); js.charset="UTF-8"; js.async = 1; js.src = p+h+u; var js2 = d.getElementsByTagName(s)[0]; js2.parentNode.insertBefore(js, js2);
                })(window, document, 'script', 'cloud.roistat.com', '46751d07945ec38697681458052a865d');
                `
          }}
        />

        {/* Скрипт для работы с куками Roistat - выполняется после загрузки основного скрипта */}
        <script
          id='roistat-cookies'
          dangerouslySetInnerHTML={{
            __html: `
                function getCookie(name) {
                  const cookies = document.cookie.split('; ');
                  for (let cookie of cookies) {
                    const [key, value] = cookie.split('=');
                    if (key === name) {
                      return decodeURIComponent(value);
                    }
                  }
                  return undefined;
                };                
                window.onRoistatAllModulesLoaded = function () {
                  window.roistat.registerOnVisitProcessedCallback(function () {
                    let attempts = 0;
                    const maxAttempts = 10;
                    const interval = setInterval(() => {
                    const rid = getCookie('_rid_anm_id');
                    if (rid || attempts >= maxAttempts) {
                      clearInterval(interval);
                      if (rid) {
                        window.roistat.setRoistatParams({'roistat_param1': rid});
                      }
                    }
                    attempts++;
                    }, 500);
                  });
                };
                `
          }}
        />

        {/* Varioqub experiments*/}
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `
                (function(e, x, pe, r, i, me, nt) {
                  e[i] = e[i] || function() {
                    (e[i].a = e[i].a || []).push(arguments);
                  };
                  me = x.createElement(pe);
                  me.async = 1;
                  me.src = r;
                  nt = x.getElementsByTagName(pe)[0];
                  me.addEventListener('error', function() {
                    function cb(t) {
                      t = t[t.length - 1];
                      if (typeof t === 'function') {
                        t({ flags: {} });
                      }
                    }
                    if (Array.isArray(e[i].a)) {
                      e[i].a.forEach(cb);
                    }
                    e[i] = function() {
                      cb(arguments);
                    };
                  });
                  nt.parentNode.insertBefore(me, nt);
                })(
                  window,document,
                  'script',
                  'https://abt.s3.yandex.net/expjs/latest/exp.js',
                  'ymab'
                  );
                ymab('metrika.92116660', 'init');
              `
          }}
        />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
        <meta
          name='application-name'
          content='Московский Институт Технологий и Управления'
        />
        <meta
          name='apple-mobile-web-app-title'
          content='Московский Институт Технологий и Управления'
        />
      </Head>
      <style jsx global>{`
        body {
          font-family: ${golosTextVF.style.fontFamily}, sans-serif;
        }
      `}</style>
      <noscript>
        <div>
          <img
            src='https://mc.yandex.ru/watch/92116660'
            style={{ position: 'absolute', left: '-9999px' }}
            alt=''
          />
        </div>
      </noscript>
      <noscript>
        <img
          src='https://vk.com/rtrg?p=VK-RTRG-1951309-9lj24'
          style={{ position: 'fixed', left: '-999px' }}
          alt=''
        />
      </noscript>
        <DefaultSeo {...SEO} />
        <LogoJsonLd
          logo={`${PATHS.front.root.index}${PATHS.front.public.assets.imgs.icons.mitmLogo512.index}`}
          url={PATHS.front.root.index}
        />
        <OrganizationJsonLd
          type='EducationalOrganization'
          id={PATHS.front.root.index}
          logo={`${PATHS.front.root.index}${PATHS.front.public.assets.imgs.icons.mitmLogo512.index}`}
          legalName='Московский Институт Технологий и Управления'
          name='Московский Институт Технологий и Управления'
          sameAs={[PATHS.front.root.index]}
          url={PATHS.front.root.index}
        />
        {/* <MentalHealthDayBanner /> */}
        <CookieConsent />
        <AskFormWidget />
        <Component {...pageProps} />
      {/* renta script */}
      <Script
        id='renta-sdk'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
                   !function(){
        var i = "renta",
            renta = window[i] = window[i] || [];

        if (!renta.initialize) {
          if (renta.invoked) {
            window.console && console.error && console.error("Renta SDK snippet included twice.");
          } else {
            renta.invoked = true;
            renta.methods = ["identify", "group", "track", "page"];
            renta.factory = function(method) {
              return function() {
                if (window[i].initialized)
                  return window[i][method].apply(window[i], arguments);

                var args = Array.prototype.slice.call(arguments);
                args.unshift(method);
                renta.push(args);
                return renta;
              };
            };

            for (var n = 0; n < renta.methods.length; n++) {
              var key = renta.methods[n];
              renta[key] = renta.factory(key);
            }

            renta.environment = function(env) {
              renta.env = env;
            };

            renta.load = function(key, domain) {
              renta.writeKey = key;
              renta.collectURL = domain || 'cdp.renta.im';

              var script = document.createElement("script");
              script.type = "text/javascript";
              script.async = true;
              script.src = "https://" + renta.collectURL + "/collect/v2/renta.min.js";

              var first = document.getElementsByTagName("script")[0];
              first.parentNode.insertBefore(script, first);
            };

            renta.SNIPPET_VERSION = "2.1";

            renta.load("faa7d3691c4545839793e9b8cc264d2f", "data.mitm.institute");
            renta.environment("prod");
            renta.page();
          }
        }
      }();
              `
        }}
      />

      {/* Top.Mail.Ru counter */}
      <script
        id='top-mail-ru-counter'
        dangerouslySetInnerHTML={{
          __html: `var _tmr = window._tmr || (window._tmr = []);
                    _tmr.push({id: "3374995", type: "pageView", start: (new Date()).getTime()});
                    (function (d, w, id) {
                      if (d.getElementById(id)) return;
                      var ts = d.createElement("script"); ts.type = "text/javascript"; ts.async = true; ts.id = id;
                      ts.src = "https://top-fwz1.mail.ru/js/code.js";
                      var f = function () {var s = d.getElementsByTagName("script")[0]; s.parentNode.insertBefore(ts, s);};
                      if (w.opera == "[object Opera]") { d.addEventListener("DOMContentLoaded", f, false); } else { f(); }
                    })(document, window, "tmr-code");`
        }}
      />
    </>
  )
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  console.log('%c_app.tsx line:233 metric', 'color: #007acc;', metric)
  switch (metric.name) {
    case 'FCP':
      console.log(
        '%c_app.tsx line:235 FCP value',
        'color: #007acc;',
        metric.value
      )
      break
    case 'LCP':
      console.log(
        '%c_app.tsx line:238 LCP value',
        'color: #007acc;',
        metric?.attribution?.lcpEntry
      )
      break
    case 'CLS':
      console.log(
        '%c_app.tsx line:241 CLS attributio',
        'color: #007acc;',
        metric
      )
      break
    case 'FID':
      console.log(
        '%c_app.tsx line:244 FID value',
        'color: #007acc;',
        metric.value
      )
      break
    case 'TTFB':
      // handle TTFB results
      break
    case 'INP':
      // handle INP results (note: INP is still an experimental metric)
      break
    default:
      break
  }
}

export default App
